import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, Phone, Mail, ChevronDown, Truck, ChevronRight, ArrowRight } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { CONTACT_INFO, categories } from '../../data/mock';
import Logo from '../Logo';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const { cartCount, openCart } = useCart();
  const location = useLocation();
  const shopRef = useRef(null);

  useEffect(() => { setIsMobileMenuOpen(false); setShopOpen(false); }, [location]);

  useEffect(() => {
    const onClick = (e) => { if (shopRef.current && !shopRef.current.contains(e.target)) setShopOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const phoneHref = `tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`;

  const navLinks = [
    { name: 'Our Story', path: '/about' },
    { name: 'Help Center', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  const linkCls = (active) => `text-[15px] font-semibold tracking-tight transition-colors ${active ? 'text-athletic-700' : 'text-dark-600 hover:text-athletic-700'}`;

  return (
    <header className="sticky top-0 left-0 right-0 z-50">
      {/* Utility bar - calm light stone */}
      <div className="bg-dark-100 text-dark-600 text-xs border-b border-dark-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between gap-4">
          <div className="flex items-center gap-5">
            <a href={phoneHref} className="flex items-center gap-1.5 hover:text-athletic-700 transition-colors"><Phone className="w-3.5 h-3.5 text-athletic-600" /> {CONTACT_INFO.phone}</a>
            <a href={`mailto:${CONTACT_INFO.email}`} className="hidden sm:flex items-center gap-1.5 hover:text-athletic-700 transition-colors"><Mail className="w-3.5 h-3.5 text-athletic-600" /> {CONTACT_INFO.email}</a>
          </div>
          <p className="flex items-center gap-1.5 font-medium"><Truck className="w-3.5 h-3.5 text-athletic-600" /> Free USA shipping &middot; 2&ndash;4 day USPS delivery</p>
        </div>
      </div>

      {/* Main row */}
      <div className="bg-white/95 backdrop-blur border-b border-dark-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[74px] gap-4">
            <Logo />

            <nav className="hidden lg:flex items-center gap-8">
              <Link to="/" className={linkCls(location.pathname === '/')}>Home</Link>
              {/* Shop dropdown */}
              <div className="relative" ref={shopRef}>
                <button onClick={() => setShopOpen(o => !o)} className={`flex items-center gap-1 ${linkCls(location.pathname === '/shop')}`}>
                  Shop <ChevronDown className={`w-4 h-4 transition-transform ${shopOpen ? 'rotate-180' : ''}`} />
                </button>
                {shopOpen && (
                  <div className="absolute left-1/2 -translate-x-1/2 mt-4 w-[440px] bg-white rounded-2xl shadow-elevated border border-dark-200 p-2.5 animate-fade-in">
                    <Link to="/shop" className="flex items-center justify-between px-4 py-3 rounded-xl bg-athletic-50 text-athletic-800 font-semibold text-sm hover:bg-athletic-100 transition-colors mb-1.5">Browse all products <ArrowRight className="w-4 h-4" /></Link>
                    <div className="grid grid-cols-1">
                      {categories.map((cat) => (
                        <Link key={cat.id} to={`/shop?category=${cat.slug}`} className="flex items-center justify-between px-4 py-2.5 rounded-xl hover:bg-dark-50 transition-colors group">
                          <span className="text-sm font-semibold text-dark-800 group-hover:text-athletic-700 transition-colors">{cat.name}</span>
                          <span className="text-xs text-dark-400">{cat.productCount} items</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {navLinks.map((link) => (
                <Link key={link.path} to={link.path} className={linkCls(location.pathname === link.path)}>{link.name}</Link>
              ))}
            </nav>

            <div className="flex items-center gap-2 sm:gap-3">
              <Link to="/shop" className="hidden xl:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-athletic-700 text-white text-sm font-semibold hover:bg-athletic-800 transition-colors">Shop Now <ArrowRight className="w-4 h-4" /></Link>
              <button
                onClick={openCart}
                data-testid="cart-button"
                className="relative w-11 h-11 rounded-full flex items-center justify-center text-dark-800 bg-dark-100 hover:bg-dark-200 transition-colors"
                aria-label="Cart"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-power-600 text-white text-[10px] font-extrabold rounded-full flex items-center justify-center border-2 border-white">
                    {cartCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden w-11 h-11 rounded-full flex items-center justify-center text-dark-900 bg-dark-100 hover:bg-dark-200"
                aria-label="Menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-dark-200 bg-white">
            <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
              <Link to="/" className="flex items-center justify-between px-4 py-3 text-base font-semibold text-dark-700 hover:bg-athletic-50 hover:text-athletic-700 rounded-xl">Home<ChevronRight className="w-4 h-4 text-dark-300" /></Link>
              <Link to="/shop" className="flex items-center justify-between px-4 py-3 text-base font-semibold text-dark-700 hover:bg-athletic-50 hover:text-athletic-700 rounded-xl">Shop All<ChevronRight className="w-4 h-4 text-dark-300" /></Link>
              <div className="pl-3">
                {categories.map((cat) => (
                  <Link key={cat.id} to={`/shop?category=${cat.slug}`} className="block px-4 py-2.5 text-sm text-dark-600 hover:bg-athletic-50 hover:text-athletic-700 rounded-xl">{cat.name}</Link>
                ))}
              </div>
              {navLinks.map((link) => (
                <Link key={link.path} to={link.path} className="flex items-center justify-between px-4 py-3 text-base font-semibold text-dark-700 hover:bg-athletic-50 hover:text-athletic-700 rounded-xl">{link.name}<ChevronRight className="w-4 h-4 text-dark-300" /></Link>
              ))}
              <a href={phoneHref} className="flex items-center gap-2 mt-3 px-4 py-3 rounded-full bg-athletic-700 text-white font-semibold justify-center"><Phone className="w-4 h-4" /> Call {CONTACT_INFO.phone}</a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
